<template>
  <header :class="['header', { 'active-mobile': activeMenu }]">
    <div class="mainContainer">
      <div class="header_wrapp flex items-center justify-between">

        <router-link :to="{ name: 'home.index' }" class="header_logo outline-none focus:outline-none">
          <img alt="" class="outline-none focus:outline-none active:outline-none" src="/image/header-logo.svg" />
        </router-link>
        <div class="header_lang hidden lg:flex">
          <button
            v-for="locale in locales"
            :key="locale.id"
            :class="[
              'semibold-font',
              { active: locale.key === selectedLocale },
            ]"
            @click="setLanguage(locale.key)"
          >
            {{ $t("language_short." + locale.name) }}
          </button>
        </div>
        <div
          v-click-outside="clickOutsideNotificationMenu"
          class="header_notification relative"
        >
          <button
            :class="[
              'header_notification_toggler',
              { active: showNotificationMenu },
            ]"
            @click="showBroadcast"
          >
            <BaseIcon icon="notification-icon" />
            <div v-if="notificationUnreadCount > 0" class="count">
              {{ notificationUnreadCount > 9 ? "!" : notificationUnreadCount }}
            </div>
          </button>
          <NotificationMenu
            :show="showNotificationMenu"
            @close="showNotificationMenu = false"
          />
        </div>
        <div class="header_menu hidden lg:flex">
          <div class="header_lang">
            <button
              v-for="loc in locales"
              :key="loc.key"
              :class="['active', { active: loc.key === locale }]"
              @click="setLanguage(loc.key)"
            >
              {{ $t("language_short." + loc.name) }}
            </button>
          </div>
          <div v-if="balance" class="header_menu_item header_balance">
            <div class="label-menu">{{ $t("balance") }}</div>
            <div class="header_balance_btn">
              <BaseIcon icon="header-balance-icon" />
              <span>{{ balance }} $</span>
            </div>
          </div>
          <div class="header_menu_item header_details">
            <div class="label-menu">{{ $t("contract") }}</div>
            <div
              v-click-outside="clickOutsideCounterpartyMenu"
              :class="[
                'header_details_wrapper',
                { active: showCounterpartyMenu }
              ]"
            >
              <button
                :class="[
                  'header_details_toggler',
                  { current: layout === 'main' },
                  { empty: emptyCounterpartyMenu },
                ]"
                :disabled="isLoadContract"
              >
                <BaseIcon v-if="!emptyCounterpartyMenu" icon="contragent-details-icon" />
                <span
                  style="margin-right: 10px"
                  v-if="activeContract?.id"
                  @click="handleContractClick"
                >{{ activeContract?.name }}</span>
              </button>
              <button
                class="header_details_arrow"
                :disabled="isLoadContract"
                @click="showCounterpartyMenu = !showCounterpartyMenu"
              ></button>
            </div>
            <CounterpartyMenu
              :show="showCounterpartyMenu"
              @close="showCounterpartyMenu = false"
              @emptyList="checkCounterpartyMenu"
            />
          </div>
          <div class="header_menu_item header_profile">
            <div class="label-menu">{{ $t("personal_cabinet") }}</div>
            <div
              v-click-outside="clickOutsideProfileMenu"
              :class="[
                'header_profile_wrapper',
                { active: showProfileMenu }
              ]"
            >
              <button
                :class="[
                  'header_profile_toggler',
                  { active: showProfileMenu },
                  { current: layout === 'profile' },
                ]"
              >
                <div class="flex items-center">
                  <BaseIcon class="green-3-color" icon="profile-icon" />
                  <span class="grey-4-color mx-2">{{ user.fullName ?? user.email }}</span>
                </div>
              </button>
              <button
                class="header_profile_arrow"
                @click="showProfileMenu = !showProfileMenu"
              ></button>
            </div>
            <ProfileMenu
              :show="showProfileMenu"
              @close="showProfileMenu = false"
            />
          </div>
        </div>
        <button
          class="header-toggler lg:hidden"
          @click="activeMenu = !activeMenu"
        >
          <i v-if="activeMenu" class="icon close-menu-icon"></i>
          <i v-else class="icon burger-menu-icon"></i>
        </button>
      </div>
    </div>
  </header>
</template>

<script>
import { readBroadcast } from "~/api/user";
import { mapGetters, mapActions } from "vuex";
import ProfileMenu from "~/components/Menu/HeaderProfileMenu.vue";
import CounterpartyMenu from "~/components/Menu/HeaderCounterpartyMenu.vue";
import NotificationMenu from "~/components/Menu/HeaderNotificationMenu.vue";
import debounce from "lodash.debounce";
import { loadLocaleMessages } from "~/plugins/i18n";
import { updateUserSettings } from "~/api/user";
import { userMixin } from "~/mixins/user";
import contractBaseMixin from "~/mixins/contractBase";

export default {
  name: "Navbar",
  props: {
    layout: String
  },
  mixins: [userMixin,
  contractBaseMixin
  ],
  components: {
    ProfileMenu,
    CounterpartyMenu,
    NotificationMenu
  },
  data() {
    return {
      showProfileMenu: false,
      showCounterpartyMenu: false,
      showNotificationMenu: false,
      activeMenu: false,
      readBroadcastDebounced: null,
      emptyCounterpartyMenu: null,
      isMobile: window.innerWidth < 992
    };
  },
  mounted() {
    if (this.activeContract) {
      this.$store.dispatch(
        "contractTransaction/fetchContractTransactions",
        this.activeContract.id
      );
    }

    window.addEventListener("resize", this.handleResize);
  },
  beforeDestroy() {
    window.removeEventListener("resize", this.handleResize);
  },
  methods: {
    ...mapActions("user", ["fetchUser", "fetchBroadcasts", "readBroadcast"]),
    ...mapActions("lang", ["loadLocaleMessages"]),
    ...mapActions("contractTransaction", ["fetchContractTransactions"]),
    showBroadcast() {
      this.showNotificationMenu = !this.showNotificationMenu;
    },
    handleResize() {
      this.isMobile = window.innerWidth < 992;
    },
    async setLanguage(language) {
      await loadLocaleMessages(language);

      let form = {
        language: this.localeValue,
        timezone: this.user.settings.timezone.timezone
      };
      updateUserSettings(form).then(() => {
        this.$store.dispatch("user/fetchUser");
      });
    },
    clickOutsideProfileMenu: function() {
      this.showProfileMenu === true ? (this.showProfileMenu = false) : "";
    },
    clickOutsideCounterpartyMenu: function() {
      this.showCounterpartyMenu === true
        ? (this.showCounterpartyMenu = false)
        : "";
    },
    clickOutsideNotificationMenu() {
      if (this.showNotificationMenu) {
        this.showNotificationMenu = false;
      }
    },
    checkCounterpartyMenu(data) {
      this.emptyCounterpartyMenu = data;
    },
    async handleContractClick() {
      if (!this.activeContract?.id || !this.activeContract?.name) {
        return;
      }

      const currentRouteName = this.$route.name;
      const currentContractName = this.$route.params.contractName;

      if (currentRouteName === "contract.cabinet.index" && currentContractName === this.activeContract.name) {
        return;
      }

      await this.setActiveContract(this.activeContract.id, this.activeContract.name);
    }
  },
  create: function() {
    this.showCountBroadcast(this.broadcasts);
  },
  computed: {
    ...mapGetters({
      balance: "contractTransaction/balance",
      user: "user/user",
      selectedLocale: "lang/locale",
      locales: "lang/locales",
      locale: "lang/locale",
      localeValue: "lang/localeValue",
      activeContract: "contract/activeContract",
      isLoadContract: "contract/isLoad",
      broadcasts: "user/broadcasts",
      notificationUnreadCount: "user/notificationUnreadCount"
    })
  },
  watch: {
    showNotificationMenu: {
      immediate: true,
      handler(newValue) {
        if (newValue && this.notificationUnreadCount > 0) {
          this.readBroadcastDebounced = debounce(() => {
            readBroadcast().then(() => {
              this.$store.dispatch("user/fetchBroadcast");
            });
          }, 800);
          this.readBroadcastDebounced();
        } else {
          if (this.readBroadcastDebounced) {
            this.readBroadcastDebounced.cancel();
          }
        }
      }
    }
  }
};
</script>
